import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { AppLayout, AuthLayout, Index, TabsLayout } from './layouts';
import { Splash } from '@/components/ui/Splash';
import NotFound from '@/screens/not-found';

/**
 * The route table — the same tree as apps/mobile's `app/` directory, written
 * out rather than derived from filenames.
 *
 * Every path matches its native counterpart exactly, so a deep link, a QR code
 * or a notification that opens `/my-tickets/abc` opens the same screen in both
 * apps. The grouping folders expo-router uses for layout — `(app)`, `(auth)`,
 * `(tabs)` — never appeared in a URL there either; here they are the nested
 * <Route> elements that wrap their children in a guard.
 *
 * Screens are code-split. A fan who signs in and looks at their ticket should
 * not have paid to download the seat map, the shop and the family console on
 * the way.
 */

/* ---- auth ---------------------------------------------------------------- */
const Welcome = lazy(() => import('@/screens/auth/welcome'));
const SignIn = lazy(() => import('@/screens/auth/sign-in'));
const ForgotPassword = lazy(() => import('@/screens/auth/forgot-password'));
const SignUp = lazy(() => import('@/screens/auth/sign-up'));
const SignUpPhone = lazy(() => import('@/screens/auth/sign-up-phone'));
const VerifyPhone = lazy(() => import('@/screens/auth/verify-phone'));
const CreateAccount = lazy(() => import('@/screens/auth/create-account'));
const AboutYou = lazy(() => import('@/screens/auth/about-you'));
const YourProfile = lazy(() => import('@/screens/auth/your-profile'));
const ParentApproval = lazy(() => import('@/screens/auth/parent-approval'));

/* ---- tabs ---------------------------------------------------------------- */
const Home = lazy(() => import('@/screens/tabs/home'));
const Match = lazy(() => import('@/screens/tabs/match'));
const Shop = lazy(() => import('@/screens/tabs/shop'));
const Community = lazy(() => import('@/screens/tabs/community'));
const Me = lazy(() => import('@/screens/tabs/me'));
const More = lazy(() => import('@/screens/tabs/more'));

/* ---- shop + commerce ----------------------------------------------------- */
const Product = lazy(() => import('@/screens/app/product/[id]'));
const AllProducts = lazy(() => import('@/screens/app/shop/all-products'));
const Cart = lazy(() => import('@/screens/app/cart'));
const Checkout = lazy(() => import('@/screens/app/checkout'));
const Order = lazy(() => import('@/screens/app/order/[id]'));

/* ---- ticketing ----------------------------------------------------------- */
const EventTickets = lazy(() => import('@/screens/app/tickets/[eventId]'));
const TicketCheckout = lazy(() => import('@/screens/app/ticket-checkout'));
const TicketConfirmed = lazy(() => import('@/screens/app/ticket-confirmed'));
const PaymentPending = lazy(() => import('@/screens/app/payment-pending'));
const PaymentFailed = lazy(() => import('@/screens/app/payment-failed'));
const MyTickets = lazy(() => import('@/screens/app/my-tickets'));
const MyTicket = lazy(() => import('@/screens/app/my-tickets/[id]'));
const Verify = lazy(() => import('@/screens/app/verify/[token]'));
const Passport = lazy(() => import('@/screens/app/passport'));
const SeasonPasses = lazy(() => import('@/screens/app/season-passes/index'));
const SeasonPassPurchase = lazy(() => import('@/screens/app/season-passes/[id]/purchase'));
const MySeasonPasses = lazy(() => import('@/screens/app/season-passes/mine'));
const Hospitality = lazy(() => import('@/screens/app/hospitality'));

/* ---- match --------------------------------------------------------------- */
const BoxScore = lazy(() => import('@/screens/app/box-score/[gameId]'));
const MatchChat = lazy(() => import('@/screens/app/match-chat/[eventId]'));
const Roster = lazy(() => import('@/screens/app/roster/index'));
const Player = lazy(() => import('@/screens/app/roster/[id]'));
const Predict = lazy(() => import('@/screens/app/predict'));
const PredictionLeaderboard = lazy(() => import('@/screens/app/prediction-leaderboard'));
const Voting = lazy(() => import('@/screens/app/voting'));

/* ---- community + creators ------------------------------------------------ */
const Channel = lazy(() => import('@/screens/app/community/[channelId]'));
const Creator = lazy(() => import('@/screens/app/creator/[id]'));
const Post = lazy(() => import('@/screens/app/post/[id]'));
const Story = lazy(() => import('@/screens/app/story/[creatorId]'));
const ComposePost = lazy(() => import('@/screens/app/compose-post'));
const ExclusiveContent = lazy(() => import('@/screens/app/exclusive-content'));
const Downloads = lazy(() => import('@/screens/app/downloads'));
const Fans = lazy(() => import('@/screens/app/fans/index'));
const Fan = lazy(() => import('@/screens/app/fans/[userId]'));
const FanFollowers = lazy(() => import('@/screens/app/fans/[userId]/followers'));
const FanFollowing = lazy(() => import('@/screens/app/fans/[userId]/following'));

/* ---- membership + loyalty ------------------------------------------------ */
const Membership = lazy(() => import('@/screens/app/membership/index'));
const MembershipCard = lazy(() => import('@/screens/app/membership/card'));
const MembershipUpgrade = lazy(() => import('@/screens/app/membership/upgrade'));
const MembershipVoucher = lazy(() => import('@/screens/app/membership/voucher'));
const Rewards = lazy(() => import('@/screens/app/rewards'));
const Redeem = lazy(() => import('@/screens/app/redeem'));
const Leaderboard = lazy(() => import('@/screens/app/leaderboard'));

/* ---- account ------------------------------------------------------------- */
const Articles = lazy(() => import('@/screens/app/articles/[slug]'));
const ArticlesList = lazy(() => import('@/screens/app/articles'));
const Notifications = lazy(() => import('@/screens/app/notifications'));
const NotificationPreferences = lazy(() => import('@/screens/app/notification-preferences'));
const EditProfile = lazy(() => import('@/screens/app/edit-profile'));
const Privacy = lazy(() => import('@/screens/app/privacy'));
const Sessions = lazy(() => import('@/screens/app/sessions'));
const Settings = lazy(() => import('@/screens/app/settings'));
const VerifyEmail = lazy(() => import('@/screens/app/verify-email'));
const Wallet = lazy(() => import('@/screens/app/wallet'));
const PaymentMethods = lazy(() => import('@/screens/app/payment-methods'));
const AddPaymentMethod = lazy(() => import('@/screens/app/payment-methods/add'));
const Family = lazy(() => import('@/screens/app/family/index'));
const FamilyMember = lazy(() => import('@/screens/app/family/[id]'));
const FamilyAdd = lazy(() => import('@/screens/app/family/add'));
const Invitations = lazy(() => import('@/screens/app/invitations/index'));
const Invitation = lazy(() => import('@/screens/app/invitations/[token]'));

export function AppRoutes() {
  return (
    <Suspense fallback={<Splash />}>
      <Routes>
        <Route index element={<Index />} />

        {/* (auth) */}
        <Route element={<AuthLayout />}>
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/sign-up-phone" element={<SignUpPhone />} />
          <Route path="/verify-phone" element={<VerifyPhone />} />
          <Route path="/create-account" element={<CreateAccount />} />
          <Route path="/about-you" element={<AboutYou />} />
          <Route path="/your-profile" element={<YourProfile />} />
          <Route path="/parent-approval" element={<ParentApproval />} />
        </Route>

        {/* (app) */}
        <Route element={<AppLayout />}>
          {/* (tabs) */}
          <Route element={<TabsLayout />}>
            <Route path="/home" element={<Home />} />
            <Route path="/match" element={<Match />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/community" element={<Community />} />
            <Route path="/me" element={<Me />} />
            <Route path="/more" element={<More />} />
          </Route>

          <Route path="/product/:id" element={<Product />} />
          <Route path="/shop/all-products" element={<AllProducts />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order/:id" element={<Order />} />

          <Route path="/tickets/:eventId" element={<EventTickets />} />
          <Route path="/ticket-checkout" element={<TicketCheckout />} />
          <Route path="/ticket-confirmed" element={<TicketConfirmed />} />
          <Route path="/payment-pending" element={<PaymentPending />} />
          <Route path="/payment-failed" element={<PaymentFailed />} />
          <Route path="/my-tickets" element={<MyTickets />} />
          <Route path="/my-tickets/:id" element={<MyTicket />} />
          <Route path="/verify/:token" element={<Verify />} />
          <Route path="/passport" element={<Passport />} />
          <Route path="/season-passes" element={<SeasonPasses />} />
          <Route path="/season-passes/mine" element={<MySeasonPasses />} />
          <Route path="/season-passes/:id/purchase" element={<SeasonPassPurchase />} />
          <Route path="/hospitality" element={<Hospitality />} />

          <Route path="/box-score/:gameId" element={<BoxScore />} />
          <Route path="/match-chat/:eventId" element={<MatchChat />} />
          <Route path="/roster" element={<Roster />} />
          <Route path="/roster/:id" element={<Player />} />
          <Route path="/predict" element={<Predict />} />
          <Route path="/prediction-leaderboard" element={<PredictionLeaderboard />} />
          <Route path="/voting" element={<Voting />} />

          <Route path="/community/:channelId" element={<Channel />} />
          <Route path="/creator/:id" element={<Creator />} />
          <Route path="/post/:id" element={<Post />} />
          <Route path="/story/:creatorId" element={<Story />} />
          <Route path="/compose-post" element={<ComposePost />} />
          <Route path="/exclusive-content" element={<ExclusiveContent />} />
          <Route path="/downloads" element={<Downloads />} />
          <Route path="/fans" element={<Fans />} />
          <Route path="/fans/:userId" element={<Fan />} />
          <Route path="/fans/:userId/followers" element={<FanFollowers />} />
          <Route path="/fans/:userId/following" element={<FanFollowing />} />

          <Route path="/membership" element={<Membership />} />
          <Route path="/membership/card" element={<MembershipCard />} />
          <Route path="/membership/upgrade" element={<MembershipUpgrade />} />
          <Route path="/membership/voucher" element={<MembershipVoucher />} />
          <Route path="/rewards" element={<Rewards />} />
          <Route path="/redeem" element={<Redeem />} />
          <Route path="/leaderboard" element={<Leaderboard />} />

          <Route path="/articles" element={<ArticlesList />} />
          <Route path="/articles/:slug" element={<Articles />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/notification-preferences" element={<NotificationPreferences />} />
          <Route path="/edit-profile" element={<EditProfile />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/sessions" element={<Sessions />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/wallet" element={<Wallet />} />
          <Route path="/payment-methods" element={<PaymentMethods />} />
          <Route path="/payment-methods/add" element={<AddPaymentMethod />} />
          <Route path="/family" element={<Family />} />
          <Route path="/family/add" element={<FamilyAdd />} />
          <Route path="/family/:id" element={<FamilyMember />} />
          <Route path="/invitations" element={<Invitations />} />
          <Route path="/invitations/:token" element={<Invitation />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
